/**
 * Capture the local desktop's screen capabilities so the agent's
 * `computer*` tools can scale model-space coordinates back to real
 * pixels.
 *
 * Canonical model: `RpaClientCapabilities` (snake_case, matches BE wire).
 * `toBackendClientCapabilities` only narrows `platform_system` for the
 * request-body type — keys already match.
 *
 * Resolution order (desktop only — web short-circuits to `null`):
 *   1. Tauri RPA bridge — authoritative (uses the OS APIs).
 *   2. `window.screen` + `window.devicePixelRatio` — best-effort
 *      geometry-only fallback for desktop builds whose RPA bridge can't
 *      answer, so a desktop is never mistaken for web.
 */

import { rpaBridge } from '@/tauri/rpa';
import { isTauri } from '@/tauri/utils';
import { bridgeErrorKind } from '@/utils/logger/bridge-error-kind';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';

import type { ClientCapabilities } from '@heidi/agents/contracts';
import {
  assembleProjectClientCapabilities,
  normalisePlatform,
  type ClientCapabilityOptions,
} from '@heidi/agents/data';
type PlatformSystem = ClientCapabilities['platform_system'];

const detectBrowserPlatform = (): PlatformSystem => {
  if (typeof navigator === 'undefined') return 'darwin';
  return normalisePlatform(navigator.userAgent);
};

/** Geometry-only wire payload when the bridge cannot advertise catalogs. */
const getBrowserCapabilities = (): ClientCapabilities | null => {
  if (typeof window === 'undefined') return null;
  return {
    platform_system: detectBrowserPlatform(),
    screen_width: window.screen?.width ?? 1920,
    screen_height: window.screen?.height ?? 1080,
    scale_factor: window.devicePixelRatio || 1,
  };
};

/**
 * Returns the best capability snapshot available as the BE wire shape, or
 * `null` when there is no desktop client attached (web, or SSR). Never
 * throws — capability detection must never block project creation.
 *
 * The presence of a snapshot is the signal the backend uses to decide
 * whether to include the desktop-only ``run_computer_use_task`` tool, so
 * the contract is deliberately binary on platform rather than on whether
 * the RPA bridge happens to answer:
 *
 *   - Web (``!isTauri()``): always ``null`` — the ONLY null path. The
 *     backend then strips computer-use and the project runs headless.
 *   - Desktop (any build): always non-null. We prefer the RPA bridge
 *     (authoritative screen geometry + catalogs), but fall back to
 *     ``window.screen`` so older desktop builds — whose ``get_capabilities``
 *     command may be missing — are never misread as web and never lose
 *     computer-use.
 */
export const getClientCapabilities = async ({
  advertiseRecordingRemoteTool = false,
}: ClientCapabilityOptions = {}): Promise<ClientCapabilities | null> => {
  if (!isTauri()) return null;

  if (rpaBridge.isAvailable()) {
    try {
      // Rust owns the catalogs (file_tools / accessibility_tools / remote_tools).
      // FE only narrows platform_system and applies the recording remote-tool
      // release gate.
      return assembleProjectClientCapabilities(await rpaBridge.getCapabilities(), {
        advertiseRecordingRemoteTool,
      });
    } catch (error) {
      // A bridge error can carry a window title, so log its kind, never its text.
      logger.warn(
        '[projects] rpaBridge.getCapabilities failed; falling back to window.screen',
        { errorKind: bridgeErrorKind(error), error: sanitizeError(error) }
      );
    }
  }
  return getBrowserCapabilities();
};

/**
 * Project capability assembly — advertises the recording remote-tool action
 * on desktop builds where the Rust bridge declares it. This is the FE gate
 * (native capability); the backend separately gates the recording child
 * per-user via `release-scribe-enable-rpa-recording`, so only cohort users
 * get the `record_computer_use_skill` sub-agent bound at create/restart.
 *
 * Never caches: screen geometry and tool catalogs can change between calls,
 * so wire payloads must always resolve fresh (a stale scale_factor means
 * mis-scaled clicks; a transient bridge failure pinned in cache would
 * permanently degrade the tool surface).
 */
export const getProjectClientCapabilities = () =>
  getClientCapabilities({ advertiseRecordingRemoteTool: true });
